/**
 * Profile card click handler - Makes cards clickable while preserving interactive elements
 */
(function() {
    'use strict';

    var SCROLL_KEY = 'search_scroll_y';
    var isSearchPage = /\/app\/search/.test(window.location.href);

    // On the search page, take over scroll restoration so the browser doesn't
    // fight with our manual restore logic.
    if (isSearchPage && 'scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
    }

    // Restore scroll position when returning to the search page via back navigation
    if (isSearchPage) {
        window.addEventListener('pageshow', function() {
            try {
                var saved = sessionStorage.getItem(SCROLL_KEY);
                if (saved !== null) {
                    var y = parseInt(saved, 10);
                    sessionStorage.removeItem(SCROLL_KEY);
                    if (y > 0) {
                        requestAnimationFrame(function() {
                            window.scrollTo(0, y);
                        });
                    }
                }
            } catch (err) {}
        });
    }

    // Validate URL to prevent XSS
    function isValidProfileUrl(url) {
        if (!url || typeof url !== 'string') return false;
        // Must start with / and not contain protocol handlers
        return url.startsWith('/') && !url.match(/^(javascript|data|vbscript|file):/i);
    }

    // Apply styles to profile cards
    function styleProfileCard(card) {
        if (card.dataset.profileUrl && isValidProfileUrl(card.dataset.profileUrl)) {
            card.style.cursor = 'pointer';
        }
    }

    // Close all profile card dropdown menus.
    // Uses the Alpine `close-dropdowns` event — every dropdown wrapper in the app
    // listens for it and resets its own menuOpen state. This avoids toggling the
    // Tailwind `.hidden` class, which permanently stuck dropdowns closed because
    // `display:none` from `.hidden` overrides Alpine's x-show.
    function closeAllDropdowns() {
        window.dispatchEvent(new CustomEvent('close-dropdowns'));
    }

    // Process newly added nodes for profile cards
    function processAddedNodes(nodes) {
        nodes.forEach(node => {
            if (!node.querySelectorAll) return;

            // Check if the node itself is a profile card
            if (node.matches && node.matches('[data-profile-card]')) {
                styleProfileCard(node);
            }

            // Check for profile cards within the node
            const cards = node.querySelectorAll('[data-profile-card]');
            cards.forEach(styleProfileCard);
        });
    }

    document.addEventListener('DOMContentLoaded', function() {
        // Close dropdown menus when clicking outside
        document.addEventListener('click', function(e) {
            // Check if click is inside a dropdown menu or its toggle button
            const dropdownMenu = e.target.closest('[data-profile-card] .relative > div.absolute');
            const dropdownToggle = e.target.closest('[data-profile-card] .relative > button');

            if (!dropdownMenu && !dropdownToggle) {
                // Click was outside all dropdown menus and toggles - close all
                closeAllDropdowns();
            }
        });

        // Save scroll position when clicking profile links on the search page
        if (isSearchPage) {
            document.body.addEventListener('click', function(e) {
                var link = e.target.closest('[data-profile-card] a[href]');
                if (link) {
                    try { sessionStorage.setItem(SCROLL_KEY, String(window.scrollY)); } catch (err) {}
                    // Restore browser scroll restoration before leaving so the
                    // destination page scrolls to top normally
                    if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
                }
            }, true);
        }

        // Event delegation for clicks
        document.body.addEventListener('click', function(e) {
            const card = e.target.closest('[data-profile-card]');
            if (!card) return;

            const profileUrl = card.dataset.profileUrl;
            if (!isValidProfileUrl(profileUrl)) return;

            // Skip if clicking interactive elements nested inside the card
            // (the card itself may be an <a> — only guard against inner interactive targets)
            const interactive = e.target.closest('a, button, input, select, textarea, [role="button"]');
            if (interactive && interactive !== card && card.contains(interactive)) return;

            // Skip if text is selected
            if (window.getSelection().toString()) return;

            // Modifier-click (cmd/ctrl) opens the full profile in a new tab —
            // preserve the browser behavior users expect on anchors, even when clicking the card body.
            // If the card itself is an anchor, let the browser handle it; otherwise open manually.
            if (e.metaKey || e.ctrlKey) {
                if (card.tagName !== 'A') {
                    try {
                        window.open(profileUrl, '_blank');
                    } catch (err) {
                        console.error('Failed to open profile in new tab:', err);
                    }
                }
                return;
            }

            // Open in side panel when the page's sidebar target opts into the preview.
            // A target marked [data-side-panel-disabled] (search list view) is skipped, so
            // those clicks navigate to the full profile page. When the preview is active
            // (search grid view), a second click on the same card (panel already showing
            // that user) navigates to the full profile.
            var panelTarget = document.querySelector('[data-side-panel-target]:not([data-side-panel-disabled])');
            if (panelTarget && panelTarget.offsetWidth > 0 && window.SidePanel && card.dataset.userId) {
                var uid = String(card.dataset.userId);
                var current = window.SidePanel.currentUserId && window.SidePanel.currentUserId();
                var currentUid = current ? String(current).split(':')[0] : null;
                if (window.SidePanel.isOpen() && currentUid === uid) {
                    // Fall through to navigation
                } else {
                    e.preventDefault();
                    window.SidePanel.open(uid, { clickSource: card.dataset.clickSource || 0, context: 'search' });
                    return;
                }
            }

            // Save scroll position when leaving search page
            if (isSearchPage) {
                try { sessionStorage.setItem(SCROLL_KEY, String(window.scrollY)); } catch (err) {}
                if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
            }

            // Navigate safely
            try {
                window.location.href = profileUrl;
            } catch (err) {
                console.error('Failed to navigate to profile:', err);
            }
        });

        // Middle-click opens the full profile in a new tab.
        document.body.addEventListener('auxclick', function(e) {
            if (e.button !== 1) return;

            const card = e.target.closest('[data-profile-card]');
            if (!card) return;

            const profileUrl = card.dataset.profileUrl;
            if (!isValidProfileUrl(profileUrl)) return;

            const interactive = e.target.closest('a, button, input, select, textarea, [role="button"]');
            if (interactive && interactive !== card && card.contains(interactive)) return;

            // Anchor cards are handled natively by the browser on middle-click.
            if (card.tagName === 'A') return;

            e.preventDefault();
            try {
                window.open(profileUrl, '_blank');
            } catch (err) {
                console.error('Failed to open profile in new tab:', err);
            }
        });

        // Style existing cards
        document.querySelectorAll('[data-profile-card]').forEach(styleProfileCard);
        
        // Observe for dynamically added cards - optimized to only check specific containers
        const observer = new MutationObserver(function(mutations) {
            const addedNodes = [];
            mutations.forEach(mutation => {
                if (mutation.addedNodes.length) {
                    addedNodes.push(...mutation.addedNodes);
                }
            });
            
            if (addedNodes.length) {
                // Debounce with requestAnimationFrame
                requestAnimationFrame(() => processAddedNodes(addedNodes));
            }
        });
        
        // Only observe containers likely to have dynamic content
        const containers = [
            document.getElementById('quick-search__results'),
            document.getElementById('aFeed'),
            document.querySelector('.main-content-col')
        ].filter(Boolean);
        
        containers.forEach(container => {
            observer.observe(container, {
                childList: true,
                subtree: true
            });
        });
        
        // Cleanup on page unload (for SPAs)
        window.addEventListener('beforeunload', () => observer.disconnect());
    });
})();